/**
 * OnboardingFabComponent — FAB flottant accessible depuis toutes les pages.
 *
 * Affiche un bouton circulaire (style "rocket") en bas à droite avec :
 *   - Pulse animation tant que l'onboarding n'est pas complet
 *   - Badge "X étapes restantes" si > 0
 *   - Ouvre le drawer principal au clic
 *
 * Caché si :
 *   - onboarding complet
 *   - state pas encore chargé
 *   - utilisateur a marqué la session "hidden"
 *   - page chat plein écran (bras droit / marketer) — sinon chevauche le composer
 *
 * Le composant est petit/léger : tout vient du service.
 */

import { CommonModule } from '@angular/common';
import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { filter } from 'rxjs/operators';

import { BusinessOnboardingService } from '../../services/business-onboarding.service';

@Component({
  selector: 'app-onboarding-fab',
  standalone: true,
  imports: [CommonModule, TranslocoModule],
  templateUrl: './onboarding-fab.component.html',
  styleUrls: ['./onboarding-fab.component.css'],
})
export class OnboardingFabComponent {
  private readonly svc = inject(BusinessOnboardingService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  /** Aligné sur vd-ai-bubble / softphone : pas de FAB sur les chats plein écran. */
  private static readonly HIDDEN_ROUTE_RE = /^\/ai-agents\/(marketer|right-hand|accountant)(\/|$|\?)/;

  private readonly onChatFullscreen = signal(
    OnboardingFabComponent.HIDDEN_ROUTE_RE.test(this.router.url.split('?')[0] || ''),
  );

  readonly visible = computed(
    () => this.svc.visibleOnSurfaces() && !this.onChatFullscreen(),
  );
  readonly progress = this.svc.progress;
  readonly remainingCount = this.svc.remainingCount;

  constructor() {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((e) => {
        const path = (e.urlAfterRedirects || e.url).split('?')[0] || '';
        this.onChatFullscreen.set(OnboardingFabComponent.HIDDEN_ROUTE_RE.test(path));
      });
  }

  open(): void {
    this.svc.openDrawer();
  }
}
